import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../../context/ToastContext';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as PaidIcon,
} from '@mui/icons-material';
import {
  fetchEventCosts,
  createEventCost,
  updateEventCost,
  deleteEventCost,
  markCostAsPaid,
  fetchEventCostsSummary,
} from '../../slices/eventsSlice';

const COST_TYPE_LABELS = {
  transportation: 'نقل',
  venue: 'قاعة',
  food: 'طعام',
  materials: 'مواد',
  equipment: 'معدات',
  speaker_fee: 'رسوم متحدث',
  decoration: 'ديكور',
  other: 'أخرى',
};

const PAYMENT_METHOD_LABELS = {
  cash: 'نقد',
  cheque: 'شيك',
  visa: 'فيزا',
  bank_transfer: 'تحويل بنكي',
};

const CostManagement = ({ eventId }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { costs, costsSummary, costsLoading } = useSelector((state) => state.events);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paidDialogOpen, setPaidDialogOpen] = useState(false);
  const [selectedCost, setSelectedCost] = useState(null);
  const [formData, setFormData] = useState({
    cost_type: '',
    description: '',
    estimated_amount: '',
    vendor_name: '',
    notes: '',
  });
  const [paidFormData, setPaidFormData] = useState({
    actual_amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: '',
    create_payment: false,
  });

  useEffect(() => {
    if (eventId) {
      dispatch(fetchEventCosts({ event: eventId }));
      dispatch(fetchEventCostsSummary(eventId));
    }
  }, [eventId, dispatch]);

  const handleOpenDialog = (cost = null) => {
    if (cost) {
      setSelectedCost(cost);
      setFormData({
        cost_type: cost.cost_type,
        description: cost.description,
        estimated_amount: cost.estimated_amount,
        vendor_name: cost.vendor_name || '',
        notes: cost.notes || '',
      });
    } else {
      setSelectedCost(null);
      setFormData({
        cost_type: '',
        description: '',
        estimated_amount: '',
        vendor_name: '',
        notes: '',
      });
    }
    setDialogOpen(true);
  };

  const handleSaveCost = async () => {
    try {
      const data = {
        event: eventId,
        ...formData,
        estimated_amount: parseFloat(formData.estimated_amount),
      };
      if (selectedCost) {
        await dispatch(updateEventCost({ id: selectedCost.id, data })).unwrap();
        showToast('تم تحديث التكلفة بنجاح', 'success');
      } else {
        await dispatch(createEventCost(data)).unwrap();
        showToast('تم إضافة التكلفة بنجاح', 'success');
      }
      setDialogOpen(false);
      dispatch(fetchEventCosts({ event: eventId }));
      dispatch(fetchEventCostsSummary(eventId));
    } catch (error) {
      showToast(error?.message || 'حدث خطأ', 'error');
    }
  };

  const handleDeleteCost = async (costId) => {
    if (window.confirm('هل أنت متأكد من حذف هذه التكلفة؟')) {
      try {
        await dispatch(deleteEventCost(costId)).unwrap();
        showToast('تم حذف التكلفة بنجاح', 'success');
        dispatch(fetchEventCosts({ event: eventId }));
        dispatch(fetchEventCostsSummary(eventId));
      } catch (error) {
        showToast(error?.message || 'حدث خطأ', 'error');
      }
    }
  };

  const handleOpenPaidDialog = (cost) => {
    setSelectedCost(cost);
    setPaidFormData({
      actual_amount: cost.estimated_amount,
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: '',
      create_payment: false,
    });
    setPaidDialogOpen(true);
  };

  const handleMarkAsPaid = async () => {
    try {
      await dispatch(
        markCostAsPaid({
          id: selectedCost.id,
          data: {
            ...paidFormData,
            actual_amount: parseFloat(paidFormData.actual_amount),
          },
        })
      ).unwrap();
      showToast('تم تسجيل الدفع بنجاح', 'success');
      setPaidDialogOpen(false);
      dispatch(fetchEventCosts({ event: eventId }));
      dispatch(fetchEventCostsSummary(eventId));
    } catch (error) {
      showToast(error?.message || 'حدث خطأ', 'error');
    }
  };

  return (
    <Box>
      {/* Cost Summary */}
      {costsSummary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  التكلفة المقدرة
                </Typography>
                <Typography variant="h6">
                  {costsSummary.total_estimated?.toFixed(2) || 0} ₪
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  التكلفة الفعلية
                </Typography>
                <Typography variant="h6">
                  {costsSummary.total_actual?.toFixed(2) || 0} ₪
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  المدفوع
                </Typography>
                <Typography variant="h6" color="success.main">
                  {costsSummary.total_paid?.toFixed(2) || 0} ₪
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  المتبقي
                </Typography>
                <Typography variant="h6" color="error.main">
                  {costsSummary.remaining?.toFixed(2) || 0} ₪
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">التكاليف</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          إضافة تكلفة
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>النوع</TableCell>
              <TableCell>الوصف</TableCell>
              <TableCell>المقدر</TableCell>
              <TableCell>الفعلي</TableCell>
              <TableCell>المورد</TableCell>
              <TableCell>الحالة</TableCell>
              <TableCell>الإجراءات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {costs.map((cost) => (
              <TableRow key={cost.id}>
                <TableCell>
                  {COST_TYPE_LABELS[cost.cost_type] || cost.cost_type}
                </TableCell>
                <TableCell>{cost.description}</TableCell>
                <TableCell>{cost.estimated_amount} ₪</TableCell>
                <TableCell>
                  {cost.actual_amount ? `${cost.actual_amount} ₪` : '-'}
                </TableCell>
                <TableCell>{cost.vendor_name || '-'}</TableCell>
                <TableCell>
                  {cost.is_paid ? (
                    <Chip label="مدفوع" color="success" size="small" />
                  ) : (
                    <Chip label="غير مدفوع" color="warning" size="small" />
                  )}
                </TableCell>
                <TableCell>
                  <Stack useFlexGap direction="row" spacing={1}>
                    <IconButton size="small" onClick={() => handleOpenDialog(cost)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {!cost.is_paid && (
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleOpenPaidDialog(cost)}
                      >
                        <PaidIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteCost(cost.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Cost Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedCost ? 'تعديل تكلفة' : 'إضافة تكلفة'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>نوع التكلفة</InputLabel>
                <Select
                  value={formData.cost_type}
                  label="نوع التكلفة"
                  onChange={(e) => setFormData({ ...formData, cost_type: e.target.value })}
                >
                  {Object.entries(COST_TYPE_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="الوصف *"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="المبلغ المقدر *"
                type="number"
                value={formData.estimated_amount}
                onChange={(e) => setFormData({ ...formData, estimated_amount: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="اسم المورد"
                value={formData.vendor_name}
                onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="ملاحظات"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>إلغاء</Button>
          <Button
            onClick={handleSaveCost}
            variant="contained"
            disabled={!formData.cost_type || !formData.description || !formData.estimated_amount}
          >
            حفظ
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark as Paid Dialog */}
      <Dialog open={paidDialogOpen} onClose={() => setPaidDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>تسجيل الدفع</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="المبلغ الفعلي *"
                type="number"
                value={paidFormData.actual_amount}
                onChange={(e) =>
                  setPaidFormData({ ...paidFormData, actual_amount: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="تاريخ الدفع *"
                type="date"
                value={paidFormData.payment_date}
                onChange={(e) =>
                  setPaidFormData({ ...paidFormData, payment_date: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>طريقة الدفع</InputLabel>
                <Select
                  value={paidFormData.payment_method}
                  label="طريقة الدفع"
                  onChange={(e) =>
                    setPaidFormData({ ...paidFormData, payment_method: e.target.value })
                  }
                >
                  {Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaidDialogOpen(false)}>إلغاء</Button>
          <Button
            onClick={handleMarkAsPaid}
            variant="contained"
            disabled={!paidFormData.actual_amount || !paidFormData.payment_date}
          >
            حفظ
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CostManagement;
